import React from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  XCircle,
  RotateCcw,
  Home,
  ChevronLeft,
  AlertTriangle,
  CreditCard,
  CheckCircle,
} from "lucide-react";

const PaymentCancelled: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const provider = searchParams.get("provider") || "Unknown";
  const planId = searchParams.get("plan_id");
  const reason = searchParams.get("reason") || "cancelled";

  const getProviderDisplayName = (provider: string) => {
    switch (provider) {
      case "paystack":
        return "Paystack";
      case "stripe":
        return "Stripe";
      default:
        return provider.charAt(0).toUpperCase() + provider.slice(1);
    }
  };

  const getPlanDisplayName = (planId: string | null) => {
    switch (planId) {
      case "PAYG":
        return "PAYG Plan";
      case "professional":
        return "Professional Plan";
      default:
        return "Subscription Plan";
    }
  };

  const getReasonMessage = (reason: string) => {
    switch (reason) {
      case "cancelled":
        return "You cancelled the payment process.";
      case "failed":
        return "Your payment could not be processed.";
      case "expired":
        return "The payment session expired.";
      case "declined":
        return "Your payment was declined by the payment provider.";
      default:
        return "The payment was not completed.";
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header - matching your app's header style */}
      <header className="border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container flex h-16 items-center">
          <Button
            variant="ghost"
            onClick={() => navigate("/pricing")}
            className="mr-4 gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>
          {/* Beta and Version Badge */}
          <div className="flex items-center gap-2 mr-4">
            <span className="text-[11px] font-medium text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/20">
              Beta
            </span>
            <span className="text-[11px] text-muted-foreground">v1.1</span>
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-semibold">Payment Status</h1>
          </div>
        </div>
      </header>

      {/* Main Content - matching your container and layout style */}
      <main className="flex-1 container py-12">
        <div className="max-w-2xl mx-auto">
          {/* Main Card - using your card styling pattern */}
          <Card className="border rounded-2xl">
            <CardHeader className="text-center pb-6">
              {/* Icon with your app's styling approach */}
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-orange-100">
                <XCircle className="h-10 w-10 text-orange-600" />
              </div>

              <CardTitle className="text-3xl font-bold text-foreground mb-3">
                Payment Not Completed
              </CardTitle>

              <p className="text-muted-foreground text-lg">
                {getReasonMessage(reason)}
              </p>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Payment Details - using your info display pattern */}
              <div className="bg-muted/30 p-6 rounded-xl space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Plan:</span>
                  <span className="font-semibold">
                    {getPlanDisplayName(planId)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">
                    Payment Provider:
                  </span>
                  <span className="font-semibold">
                    {getProviderDisplayName(provider)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Status:</span>
                  <Badge variant="destructive" className="text-xs">
                    {reason.charAt(0).toUpperCase() + reason.slice(1)}
                  </Badge>
                </div>
              </div>

              {/* Action Buttons - using your button styling */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button
                  onClick={() => navigate("/pricing")}
                  className="flex-1 text-lg py-6"
                  size="lg"
                >
                  <RotateCcw className="h-5 w-5 mr-2" />
                  Try Again
                </Button>

                <Button
                  variant="outline"
                  onClick={() => navigate("/")}
                  className="flex-1 text-lg py-6"
                  size="lg"
                >
                  <Home className="h-5 w-5 mr-2" />
                  Go to Dashboard
                </Button>
              </div>

              {/* Help Section - matching your info cards */}
              <div className="pt-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Alternative Options
                </h3>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></div>
                    <div className="flex-1">
                      <p className="font-medium">
                        We support multiple payment providers for your
                        convenience.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></div>
                    <div className="flex-1">
                      <p className="font-medium">
                        Our team is here to help resolve any payment issues.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></div>
                    <div className="flex-1">
                      <p className="font-medium">
                        You can always upgrade later when ready.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer - matching your subscription plans footer */}
      <footer className="py-6 px-6 text-center text-sm text-muted-foreground border-t">
        <div className="max-w-2xl mx-auto">
          <p className="mb-2">Need help with your payment?</p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center items-center">
            <a
              href="mailto:support@myhu.world"
              className="text-primary hover:underline font-medium"
            >
              Email: support@myhu.world
            </a>
            <span className="hidden sm:inline text-muted-foreground">•</span>
            <span>Available 24/7</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PaymentCancelled;
